/**
 * Owner Dashboard redesign (AB#2206) — below the fold: period KPIs, one combined
 * bookings + revenue chart, booking outcomes and payments. All from the existing
 * dashboard summary (GET /salon-owner/dashboard), salon-wide.
 */
import React, { useId } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { fill } from '../../../lib/staffDisplay';
import { STATUS_TONE_COLORS } from '../../../lib/bookingLifecycle';
import type { DateRangeKey } from '../../../hooks/useDashboard';
import type { PaymentMethodBreakdown, RecentPaymentDto } from '../../../types/api-responses';
import {
    CANCELLATION_WARNING_PCT, dailyLabel, isCancellationWarning, niceMax, outcomeShares,
} from './dashboardModel';
import { formatMoney, formatShortDay } from './dashboardFormat';
import s from './Dashboard.module.css';

export type PerfRange = Extract<DateRangeKey, 'last7days' | 'last30days' | 'thismonth'>;

export const PERF_RANGES: { key: PerfRange; labelKey: string }[] = [
    { key: 'last7days', labelKey: 'odash.perf.period.7' },
    { key: 'last30days', labelKey: 'odash.perf.period.30' },
    { key: 'thismonth', labelKey: 'odash.perf.period.month' },
];

const METHOD_KEY: Record<string, string> = {
    cash: 'dash.payments.method.cash',
    card: 'dash.payments.method.card',
    online: 'dash.payments.method.online',
};

export interface PerformanceProps {
    range: PerfRange;
    onRange: (r: PerfRange) => void;
    loaded: boolean;
    error: boolean;
    onRetry: () => void;
    currency: string;
    totalAppointments: number;
    revenue: number;
    labels: string[];
    bookings: number[];
    amounts: number[];
    statLabels: string[];
    statValues: number[];
    recentPayments: RecentPaymentDto[];
    breakdown: PaymentMethodBreakdown[];
    branchSelected: boolean;
}

/* ── Combined chart ──────────────────────────────────────────────── */

const W = 640;
const H = 200;
const PAD = { top: 12, right: 44, bottom: 26, left: 30 };

const BookingsRevenueChart: React.FC<{
    labels: string[]; bookings: number[]; amounts: number[]; currency: string; year: number;
}> = ({ labels, bookings, amounts, currency, year }) => {
    const { t, language } = useLanguage();
    const titleId = useId();
    const n = labels.length;
    const maxB = niceMax(Math.max(0, ...bookings));
    const maxR = niceMax(Math.max(0, ...amounts));
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;
    const step = n > 0 ? innerW / n : innerW;
    const barW = Math.max(2, Math.min(14, step * 0.6));
    const x = (i: number) => PAD.left + step * i + step / 2;
    const yB = (v: number) => PAD.top + innerH - (v / maxB) * innerH;
    const yR = (v: number) => PAD.top + innerH - (v / maxR) * innerH;
    const ticks = [0, 0.5, 1];
    const linePts = amounts.map((a, i) => `${x(i).toFixed(1)},${yR(a).toFixed(1)}`).join(' ');
    const lastPaid = amounts.reduce((idx, a, i) => (a > 0 ? i : idx), -1);
    const xLabelIdx = n > 0 ? Array.from(new Set([0, Math.floor((n - 1) / 2), n - 1])) : [];
    // RTL: the time axis keeps reading oldest → newest left to right (chart convention).
    return (
        <svg
            className={s.chart}
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-labelledby={titleId}
            direction="ltr"
        >
            <title id={titleId}>{t('odash.chart.title')}</title>
            <g className={s.chartGrid}>
                {ticks.map(f => (
                    <line key={f} x1={PAD.left} x2={W - PAD.right} y1={PAD.top + innerH * (1 - f)} y2={PAD.top + innerH * (1 - f)} />
                ))}
            </g>
            <g className={s.chartAxis}>
                {ticks.map(f => (
                    <text key={`b${f}`} x={PAD.left - 6} y={PAD.top + innerH * (1 - f) + 4} textAnchor="end">
                        {Math.round(maxB * f)}
                    </text>
                ))}
                {ticks.map(f => (
                    <text key={`r${f}`} x={W - PAD.right + 6} y={PAD.top + innerH * (1 - f) + 4} textAnchor="start">
                        {Math.round(maxR * f).toLocaleString('en-GB')}
                    </text>
                ))}
                {xLabelIdx.map(i => (
                    <text key={`x${i}`} x={x(i)} y={H - 6} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>
                        {dailyLabel(labels[i], year, language)}
                    </text>
                ))}
            </g>
            <g className={s.chartBars}>
                {bookings.map((b, i) => (b > 0 ? (
                    <rect key={i} x={x(i) - barW / 2} y={yB(b)} width={barW} height={PAD.top + innerH - yB(b)} rx={2}>
                        <title>
                            {fill(t('odash.chart.point'), {
                                date: dailyLabel(labels[i], year, language),
                                bookings: b,
                                revenue: formatMoney(amounts[i] ?? 0, currency),
                            })}
                        </title>
                    </rect>
                ) : null))}
            </g>
            {n > 1 && <polyline className={s.chartLine} points={linePts} />}
            {lastPaid >= 0 && <circle className={s.chartDot} cx={x(lastPaid)} cy={yR(amounts[lastPaid])} r={3.5} />}
        </svg>
    );
};

/* ── Section ─────────────────────────────────────────────────────── */

export const PerformanceSection: React.FC<PerformanceProps> = ({
    range, onRange, loaded, error, onRetry, currency, totalAppointments, revenue,
    labels, bookings, amounts, statLabels, statValues, recentPayments, breakdown, branchSelected,
}) => {
    const { t, language } = useLanguage();
    const shares = outcomeShares(statLabels, statValues);
    const warn = loaded && totalAppointments > 0 && isCancellationWarning(shares.cancelled);
    const paymentCount = breakdown.reduce((sum, b) => sum + (Number(b.count) || 0), 0);
    const hasBookings = bookings.some(b => b > 0);
    const year = new Date().getFullYear();
    const v = (x: React.ReactNode) => (loaded ? x : '—');

    const outcomes = [
        { key: 'completed', pct: shares.completed, color: STATUS_TONE_COLORS.completed.fg, labelKey: 'odash.outcomes.completed' },
        { key: 'confirmed', pct: shares.confirmed, color: STATUS_TONE_COLORS.confirmed.fg, labelKey: 'odash.outcomes.confirmed' },
        { key: 'pending', pct: shares.pending, color: STATUS_TONE_COLORS.pending.fg, labelKey: 'odash.outcomes.pending' },
        { key: 'cancelled', pct: shares.cancelled, color: STATUS_TONE_COLORS.ended.fg, labelKey: 'odash.outcomes.cancelled' },
    ];

    return (
        <section className={s.perf} aria-labelledby="dash-perf-title">
            <header className={s.perfHead}>
                <div className={s.cardHeadText}>
                    <h2 id="dash-perf-title" className={s.sectionTitle}>{t('odash.perf.title')}</h2>
                    {branchSelected && <span className={s.cardSub}>{t('odash.perf.scope')}</span>}
                </div>
                <div className={s.segmented} role="radiogroup" aria-label={t('odash.perf.period')}>
                    {PERF_RANGES.map(r => (
                        <button
                            key={r.key}
                            type="button"
                            role="radio"
                            aria-checked={range === r.key}
                            className={range === r.key ? s.segOn : s.seg}
                            onClick={() => onRange(r.key)}
                        >
                            {t(r.labelKey)}
                        </button>
                    ))}
                </div>
            </header>

            {error && (
                <div className={s.cardError} role="alert">
                    <span>{t('odash.perf.loadFailed')}</span>
                    <button type="button" className={s.btnSecondary} onClick={onRetry}>{t('odash.retry')}</button>
                </div>
            )}

            <div className={s.kpis}>
                <div className={s.kpi}>
                    <span className={s.kpiLabel}>{t('odash.kpi.revenue')}</span>
                    <span className={s.kpiValue}>{v(formatMoney(revenue, currency))}</span>
                    <span className={s.kpiSub}>{loaded ? fill(t('odash.kpi.revenueSub'), { count: paymentCount }) : ''}</span>
                </div>
                <div className={s.kpi}>
                    <span className={s.kpiLabel}>{t('odash.kpi.bookings')}</span>
                    <span className={s.kpiValue}>{v(totalAppointments)}</span>
                    <span className={s.kpiSub}>{t('odash.kpi.bookingsSub')}</span>
                </div>
                <div className={s.kpi}>
                    <span className={s.kpiLabel}>{t('odash.kpi.completed')}</span>
                    <span className={s.kpiValue}>{v(`${shares.completed}%`)}</span>
                    <span className={s.share} aria-hidden="true"><i className={s.shareOk} style={{ inlineSize: `${shares.completed}%` }} /></span>
                    <span className={s.kpiSub}>{t('odash.kpi.shareSub')}</span>
                </div>
                <div className={`${s.kpi} ${warn ? s.kpiWarn : ''}`} data-testid="dash-kpi-cancelled" data-warn={warn ? 'true' : 'false'}>
                    <span className={s.kpiLabel}>{t('odash.kpi.cancelled')}</span>
                    <span className={`${s.kpiValue} ${warn ? s.valueDanger : ''}`}>{v(`${shares.cancelled}%`)}</span>
                    <span className={s.share} aria-hidden="true"><i className={s.shareBad} style={{ inlineSize: `${shares.cancelled}%` }} /></span>
                    <span className={`${s.kpiSub} ${warn ? s.kpiSubWarn : ''}`}>
                        {warn ? fill(t('odash.kpi.cancelledWarn'), { pct: CANCELLATION_WARNING_PCT }) : t('odash.kpi.shareSub')}
                    </span>
                </div>
            </div>

            <div className={s.perfGrid}>
                <section className={s.card} aria-labelledby="dash-chart-title">
                    <header className={s.cardHead}>
                        <div className={s.cardHeadText}>
                            <h3 id="dash-chart-title" className={s.cardTitle}>{t('odash.chart.title')}</h3>
                            <span className={s.cardSub}>{t('odash.chart.sub')}</span>
                        </div>
                        <div className={s.legend} aria-hidden="true">
                            <span><i className={s.legendBar} />{t('odash.chart.bookings')}</span>
                            <span><i className={s.legendLine} />{fill(t('odash.chart.revenue'), { currency })}</span>
                        </div>
                    </header>
                    <div className={s.chartBox}>
                        {!loaded && <div className={`${s.chartSkeleton} ${s.skeleton}`} />}
                        {loaded && !hasBookings && <p className={`${s.muted} ${s.chartEmpty}`}>{t('odash.chart.empty')}</p>}
                        {loaded && hasBookings && (
                            <BookingsRevenueChart labels={labels} bookings={bookings} amounts={amounts} currency={currency} year={year} />
                        )}
                    </div>
                </section>

                <section className={s.card} aria-labelledby="dash-outcomes-title">
                    <header className={s.cardHead}>
                        <div className={s.cardHeadText}>
                            <h3 id="dash-outcomes-title" className={s.cardTitle}>{t('odash.outcomes.title')}</h3>
                            {loaded && <span className={s.cardSub}>{fill(t('odash.outcomes.sub'), { count: totalAppointments })}</span>}
                        </div>
                    </header>
                    <div className={s.outcomes}>
                        <div className={s.stack} aria-hidden="true">
                            {loaded && totalAppointments > 0 && outcomes.map(o => (
                                o.pct > 0 ? <i key={o.key} style={{ inlineSize: `${o.pct}%`, background: o.color }} /> : null
                            ))}
                        </div>
                        <ul className={s.outcomeList}>
                            {outcomes.map(o => (
                                <li key={o.key} className={s.outcome}>
                                    <i style={{ background: o.color }} aria-hidden="true" />
                                    <span>{t(o.labelKey)}</span>
                                    <b>{v(`${o.pct}%`)}</b>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            </div>

            <section className={s.card} aria-labelledby="dash-pay-title">
                <header className={s.cardHead}>
                    <h3 id="dash-pay-title" className={s.cardTitle}>{t('odash.pay.title')}</h3>
                    <Link to="/payments" className={s.link}>{t('odash.pay.all')}</Link>
                </header>
                {loaded && recentPayments.length === 0 && breakdown.length === 0 ? (
                    <p className={`${s.muted} ${s.cardPad}`}>{t('odash.pay.empty')}</p>
                ) : (
                    <div className={s.pay}>
                        <div className={s.payCol}>
                            <h4 className={s.overline}>{t('odash.pay.latest')}</h4>
                            <ul className={s.payList}>
                                {recentPayments.slice(0, 3).map(p => (
                                    <li key={p.id} className={s.payRow}>
                                        <span className={s.muted}>
                                            {fill(t('dash.payments.appointmentRef'), { id: p.appointmentId })}
                                            {p.customerName ? <> · <bdi>{p.customerName}</bdi></> : null}
                                            {p.date ? <> · {formatShortDay(p.date, language)}</> : null}
                                        </span>
                                        <b>{formatMoney(Number(p.amount), p.currency || currency)}</b>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className={s.payCol}>
                            <h4 className={s.overline}>{t('odash.pay.byMethod')}</h4>
                            <ul className={s.payList}>
                                {breakdown.map(b => (
                                    <li key={b.method} className={s.payRow}>
                                        <span className={s.muted}>
                                            {METHOD_KEY[b.method?.toLowerCase()] ? t(METHOD_KEY[b.method.toLowerCase()]) : b.method}
                                            {' · '}
                                            {fill(t('odash.kpi.revenueSub'), { count: b.count })}
                                        </span>
                                        <b>{formatMoney(Number(b.amount), currency)}</b>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </section>
        </section>
    );
};
